/**
 * Universal per-row Issues chip used by every exception table.
 *
 * Communicates the row's lifecycle state via colour + icon + aria-label
 * so the chip is the single canonical surface for row status (the
 * per-row "status pill" column is intentionally absent).
 *
 * Click behaviour: the chip is the primary trigger for the per-row
 * ``ExceptionDrawer`` - clicking calls ``onOpenDrawer``. There is no
 * popover; the drawer's "Other issues on this item" section now shows
 * the entry list that used to live in the chip's popover.
 *
 * Hover: multi-line summary tooltip (open/blocked/marked-fixed counts,
 * oldest-open age, notes - see ``tooltipLines``), with the block reason
 * on a separately-styled trailing line. Non-interactive content only
 * so MUI Tooltip dismissal stays clean.
 *
 * Empty state: an outlined dash chip at the same footprint as the
 * populated chips - secondary in tone but not washed out, and always
 * clickable (when the row can anchor a drawer) so the closed history
 * and notes stay one click away.
 *
 * State resolution + palette + sx come from ``./issuesState`` so the
 * drawer's co-issues section shares one source of truth with the chip.
 */
import React from 'react'
import { Box, Chip, Tooltip } from '@mui/material'
import StickyNote2Icon from '@mui/icons-material/StickyNote2'
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import type { SvgIconComponent } from '@mui/icons-material'
import {
  ariaLabelFor,
  chipSxFor,
  FIX_STATE_META,
  fixMarkerTooltipNode,
  iconFor,
  resolveState,
  tableFixMarker,
  tooltipLines,
  type IssuesChipEntry,
} from './issuesState'

// Re-export the entry type so callers don't reach into issuesState
// directly when they only need the row shape.
export type { IssuesChipEntry } from './issuesState'

export interface IssuesChipProps {
  errors: IssuesChipEntry[] | undefined
  recentlyClosed: IssuesChipEntry[] | undefined
  /** Number of notes anchored to this row's (exception, object). */
  noteCount?: number
  /** Number of notes on the same object that live on a *different*
   * exception type. When this row has no notes of its own but a sibling
   * exception does, the chip shows a hollow "related" dot. */
  siblingNoteCount?: number
  /** Open the per-row ``ExceptionDrawer``. The chip is the primary
   * trigger; row-click is a secondary affordance handled at the table
   * level. */
  onOpenDrawer?: () => void
}

const TooltipBody: React.FC<{
  lines: string[]
  blockReason?: string
}> = ({ lines, blockReason }) => (
  <Box sx={{ py: 0.25 }}>
    {lines.map((line, i) => (
      <Box
        key={i}
        sx={{
          fontWeight: i === 0 ? 700 : 400,
          fontSize: i === 0 ? '0.75rem' : '0.7rem',
          color: '#FFFFFF',
          opacity: i === 0 ? 0.95 : 0.85,
          mt: i === 0 ? 0 : 0.25,
        }}
      >
        {line}
      </Box>
    ))}
    {blockReason ? (
      <Box sx={{ fontSize: '0.7rem', opacity: 0.85, mt: 0.25, fontStyle: 'italic' }}>
        "{blockReason}"
      </Box>
    ) : null}
  </Box>
)

const RELATED_NOTE_HINT = 'Notes on a related exception'

const IssuesChip: React.FC<IssuesChipProps> = ({
  errors,
  recentlyClosed,
  noteCount = 0,
  siblingNoteCount = 0,
  onOpenDrawer,
}) => {
  const s = resolveState(errors, recentlyClosed)
  const hasNotes = noteCount > 0
  // Only when this row has no notes of its own - a direct note always
  // wins the dot, so the "related" hollow dot never competes with it.
  const hasRelatedNotes = !hasNotes && siblingNoteCount > 0

  // Empty state: secondary dash chip, same footprint as the populated
  // chips. Always opens the drawer when the row can anchor one (the table
  // withholds ``onOpenDrawer`` from rows without a lifecycle tuple) - a
  // zero-issue object still has its closed history / notes one click away.
  if (s.state === 'empty') {
    const clickable = !!onOpenDrawer
    const label = hasNotes ? String(noteCount) : '-'
    const noteFragment = hasNotes
      ? ` · ${noteCount} ${noteCount === 1 ? 'note' : 'notes'}`
      : hasRelatedNotes
        ? ` · ${RELATED_NOTE_HINT}`
        : ''
    const tooltipText = clickable
      ? `No open issues${noteFragment} · Open details`
      : `No open issues${noteFragment}`
    const emptySx = chipSxFor('empty')
    return (
      <Tooltip title={tooltipText} arrow placement="right" enterDelay={250}>
        <Box component="span" sx={{ display: 'inline-flex', position: 'relative' }}>
          <Chip
            label={label}
            size="small"
            variant="outlined"
            aria-label={tooltipText}
            onClick={
              clickable
                ? (e) => {
                    e.stopPropagation()
                    onOpenDrawer?.()
                  }
                : undefined
            }
            sx={{ ...emptySx, cursor: clickable ? 'pointer' : 'default' }}
          />
          {hasNotes ? <NoteBadge /> : hasRelatedNotes ? <NoteBadge variant="related" /> : null}
        </Box>
      </Tooltip>
    )
  }

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    onOpenDrawer?.()
  }

  // Insert the related-exception hint above the trailing "Click to
  // inspect" line when this row has no notes of its own but a sibling
  // exception on the same object does.
  const lines = tooltipLines(s, noteCount)
  if (hasRelatedNotes) lines.splice(lines.length - 1, 0, RELATED_NOTE_HINT)

  // Marked-fixed PROGRESS marker: a small corner pennant on the chip's top-left
  // (mirroring the note badge's top-right), so it never widens the Issues column.
  // Teal AssignmentTurnedIn while awaiting the next sync; red AssignmentLate once
  // a later sync still detected one. Its own tooltip carries the detail; the main
  // chip keeps the worst lifecycle state.
  const marker = tableFixMarker(s)

  return (
    // One inline-flex control: the pennant + note badge are absolute children of
    // this relative wrapper, anchored to the chip's corners without adding width.
    <Box
      component="span"
      sx={{ display: 'inline-flex', position: 'relative', overflow: 'visible' }}
    >
      <Tooltip
        title={<TooltipBody lines={lines} blockReason={s.blockReason} />}
        arrow
        placement="right"
        enterDelay={250}
      >
        {/* span wrapper lets the Tooltip attach without intercepting the chip's onClick. */}
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <Chip
            icon={iconFor(s.state) as React.ReactElement}
            label={String(s.count)}
            size="small"
            variant={s.state === 'resolved' ? 'outlined' : 'filled'}
            onClick={handleOpen}
            aria-label={ariaLabelFor(s)}
            sx={chipSxFor(s.state)}
          />
        </Box>
      </Tooltip>
      {marker ? (
        <Tooltip
          title={fixMarkerTooltipNode(marker.tooltip)}
          arrow
          placement="top"
          enterDelay={250}
        >
          <CornerBadge
            Icon={FIX_STATE_META[marker.state].Icon}
            color={FIX_STATE_META[marker.state].color}
            side="left"
            ariaLabel={marker.ariaLabel}
            interactive
            onClick={handleOpen}
          />
        </Tooltip>
      ) : null}
      {hasNotes ? <NoteBadge /> : hasRelatedNotes ? <NoteBadge variant="related" /> : null}
    </Box>
  )
}

// Mid-grey: notes are neutral metadata, not a status. Keeping the note off the
// brand palette reserves teal for "awaiting fix" and red for "failed fix" so the
// chip carries one colour per state, not three competing teals/reds.
const NOTE_FG = '#5F6B70'

/**
 * One shared corner badge for the chip's two markers, mirrored into either top
 * corner so the pair reads as one family: a small filled rounded-rect with a
 * white glyph and a page-colour "cut-out" border that lifts it off the red /
 * amber / teal pill behind it.
 *  - The fix pennant (``side="left"``) is solid teal / red (``FIX_STATE_META``)
 *    and ``interactive`` (its own Tooltip + click-to-open).
 *  - The note badge (``side="right"``) is solid grey; its ``hollow`` variant
 *    (related notes) flips to a white fill + grey glyph/border so it reads as a
 *    lighter "context lives elsewhere" cue.
 * forwardRef + props-spread-first so the interactive pennant can be a Tooltip
 * child (Tooltip injects ``aria-label={null}`` for a node title, so role +
 * aria-label are pinned AFTER the spread to win). */
const CornerBadge = React.forwardRef<
  HTMLSpanElement,
  {
    Icon: SvgIconComponent
    color: string
    side: 'left' | 'right'
    hollow?: boolean
    /** When set, the badge is a labelled image; otherwise it's aria-hidden
     *  (the count + intent live in the chip's tooltip). */
    ariaLabel?: string
    interactive?: boolean
  } & React.HTMLAttributes<HTMLSpanElement>
>(({ Icon, color, side, hollow = false, ariaLabel, interactive = false, ...props }, ref) => (
  <Box
    ref={ref}
    component="span"
    {...props}
    {...(ariaLabel ? { role: 'img', 'aria-label': ariaLabel } : { 'aria-hidden': true })}
    sx={{
      position: 'absolute',
      top: -7,
      [side]: -9,
      zIndex: 1,
      width: 19,
      height: 15,
      borderRadius: '4px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: hollow ? 'background.paper' : color,
      border: '1.5px solid',
      borderColor: hollow ? color : 'background.paper',
      cursor: interactive ? 'pointer' : 'default',
      pointerEvents: interactive ? undefined : 'none',
    }}
  >
    <Icon sx={{ fontSize: 12, color: hollow ? color : '#FFFFFF' }} />
  </Box>
))
CornerBadge.displayName = 'CornerBadge'

/** Notes-present badge on the chip's top-right - a neutral grey ``CornerBadge``.
 * - ``direct`` (default): solid grey, filled note glyph - notes on THIS exception.
 * - ``related``: hollow (white fill, grey outline), outlined note glyph - notes
 *   live only on a *different* exception for the same object. */
const NoteBadge: React.FC<{ variant?: 'direct' | 'related' }> = ({
  variant = 'direct',
}) => (
  <CornerBadge
    Icon={variant === 'related' ? StickyNote2OutlinedIcon : StickyNote2Icon}
    color={NOTE_FG}
    side="right"
    hollow={variant === 'related'}
  />
)

export default IssuesChip
